/**
 * Video Call Utilities for AWS Chime SDK
 * Provides resilience, retry logic, and connection monitoring helpers
 */

import { MeetingSessionStatusCode } from "amazon-chime-sdk-js";

// Constants for retry and timeout configurations
export const VIDEO_CALL_CONFIG = {
  // Retry configurations
  MAX_AUDIO_BIND_RETRIES: 5,
  MAX_DEVICE_INIT_RETRIES: 3,
  MAX_RECONNECTION_ATTEMPTS: 5,

  // Timeout configurations (in ms)
  AUDIO_BIND_RETRY_DELAY: 1500,
  DEVICE_INIT_RETRY_DELAY: 1000,
  RECONNECTION_BASE_DELAY: 2000,
  CONNECTION_TIMEOUT: 30000,
  AUDIO_RECOVERY_DELAY: 2000,
  DEVICE_REFRESH_DELAY: 1000,

  // Network quality thresholds (fraction of audio packets lost in one
  // metric window — typically 1 second). Higher thresholds avoid flagging
  // brief Wi-Fi / WebRTC blips on otherwise-healthy connections.
  POOR_CONNECTION_PACKET_LOSS_THRESHOLD: 0.15, // 15%
  CRITICAL_CONNECTION_PACKET_LOSS_THRESHOLD: 0.35, // 35%

  // How many sustained poor-quality samples (in a sliding window) before we
  // escalate to CRITICAL and trigger a full reconnection.
  POOR_CONNECTION_RECONNECT_THRESHOLD: 8,
  // Number of consecutive good metric windows needed to decay the
  // poor-connection counter back toward GOOD.
  POOR_CONNECTION_DECAY_PER_GOOD_TICK: 1,

  // Audio monitoring — silence checks. A medical consult routinely has
  // 30+ seconds of quiet (typing, reading notes, examination). Old values
  // (5s × 3 = 15s) generated frequent false positives. New values give us
  // 10s × 6 = 60s before we conclude audio has actually stopped.
  AUDIO_CHECK_INTERVAL: 10000,
  AUDIO_SILENCE_THRESHOLD: 6,
};

/**
 * What to do when Chime reports the session has stopped. By the time
 * audioVideoDidStop fires, Chime has already given up on its own internal
 * reconnection, so a recoverable failure needs the session started again.
 */
export const SessionStopAction = {
  IGNORE: "ignore",
  RECONNECT: "reconnect",
  JOINED_ELSEWHERE: "joined_elsewhere",
  GIVE_UP: "give_up",
};

// The call ended on purpose: the user left, or the meeting was closed.
// AudioAttendeeRemoved is the backend deleting this attendee, and
// NoAttendeePresent a join to a meeting that is already over; neither is a
// fault a reconnect could fix, and reporting them as one hid real failures
// (KIN-1987).
const NORMAL_STOP_CODES = [
  MeetingSessionStatusCode.OK,
  MeetingSessionStatusCode.Left,
  MeetingSessionStatusCode.MeetingEnded,
  MeetingSessionStatusCode.AudioAttendeeRemoved,
  MeetingSessionStatusCode.NoAttendeePresent,
];

export const decideSessionStopAction = (sessionStatus) => {
  const statusCode = sessionStatus?.statusCode?.();

  if (statusCode === undefined || NORMAL_STOP_CODES.includes(statusCode)) {
    return SessionStopAction.IGNORE;
  }

  // The same attendee joined from another tab or device. Reconnecting here
  // would just knock that session off in turn.
  if (statusCode === MeetingSessionStatusCode.AudioJoinedFromAnotherDevice) {
    return SessionStopAction.JOINED_ELSEWHERE;
  }

  if (sessionStatus.isTerminal?.()) {
    return SessionStopAction.GIVE_UP;
  }

  return SessionStopAction.RECONNECT;
};

// Cameras that show up in the list but send nothing useful to a call: the
// infrared sensor behind Windows Hello, and virtual cameras whose app isn't
// running (both send blank frames). Common on ASUS and other Windows laptops.
const UNSUITABLE_CAMERA_LABEL =
  /\b(ir|infrared)\b|windows hello|virtual|\bobs\b|snap camera|manycam|xsplit|nvidia broadcast|\bndi\b/i;

export const isUnsuitableCamera = (device) =>
  UNSUITABLE_CAMERA_LABEL.test(device?.label || "");

/**
 * The camera to start: the user's saved choice, else the camera the browser
 * itself opened for the permission check, else the first one that isn't an
 * IR or virtual camera. The list order is the OS's, not a preference — on many
 * laptops the IR camera comes first.
 */
export const pickCameraDevice = (
  devices,
  { savedDeviceId = null, browserDefaultDeviceId = null } = {}
) => {
  if (!devices || devices.length === 0) return null;

  const findById = (deviceId) =>
    deviceId ? devices.find((device) => device.deviceId === deviceId) : null;

  return (
    findById(savedDeviceId) ||
    findById(browserDefaultDeviceId) ||
    devices.find((device) => !isUnsuitableCamera(device)) ||
    devices[0]
  );
};

/**
 * Connection state enum for tracking meeting connection
 */
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
};

/**
 * Network quality levels
 */
export const NetworkQuality = {
  EXCELLENT: 'excellent',
  GOOD: 'good',
  FAIR: 'fair',
  POOR: 'poor',
  CRITICAL: 'critical',
};

/**
 * Retry with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @returns {Promise} - Result of the function
 */
export const retryWithBackoff = async (
  fn,
  {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    onRetry = null,
    shouldRetry = () => true,
  } = {}
) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn(attempt);
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
      const jitter = Math.random() * 0.3 * delay; // Add 0-30% jitter

      if (onRetry) {
        onRetry(attempt + 1, delay + jitter, error);
      }

      await sleep(delay + jitter);
    }
  }

  throw lastError;
};

/**
 * Sleep utility
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Create a timeout promise
 * @param {number} ms - Timeout in milliseconds
 * @param {string} message - Error message
 * @returns {Promise}
 */
export const createTimeout = (ms, message = 'Operation timed out') => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(message)), ms);
  });
};

/**
 * Execute with timeout
 * @param {Promise} promise - Promise to execute
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} message - Timeout error message
 * @returns {Promise}
 */
export const withTimeout = (promise, timeoutMs, message) => {
  return Promise.race([promise, createTimeout(timeoutMs, message)]);
};

/**
 * Audio element health checker
 */
export class AudioHealthMonitor {
  constructor(audioElement, options = {}) {
    this.audioElement = audioElement;
    this.options = {
      onSilenceDetected: options.onSilenceDetected || (() => {}),
      onAudioRecovered: options.onAudioRecovered || (() => {}),
      onAudioError: options.onAudioError || (() => {}),
      checkInterval: options.checkInterval || VIDEO_CALL_CONFIG.AUDIO_CHECK_INTERVAL,
      silenceThreshold: options.silenceThreshold || VIDEO_CALL_CONFIG.AUDIO_SILENCE_THRESHOLD,
    };

    this.silentCheckCount = 0;
    this.wasAudioActive = false;
    this.checkIntervalId = null;
    this.audioContext = null;
    this.analyser = null;
    // Latches once we've fired onSilenceDetected for the current silence
    // streak so we don't keep re-firing every tick after the threshold is
    // crossed. Reset when audio recovers.
    this.silenceReported = false;
  }

  start() {
    if (this.checkIntervalId) return;

    this.setupAnalyser();
    this.checkIntervalId = setInterval(() => this.checkAudioHealth(), this.options.checkInterval);
  }

  stop() {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }

  // The other person left: their side going quiet is expected, so start over
  // as if nothing had been heard yet — silence counts again only once someone
  // speaks (KIN-1987).
  resetSilenceTracking() {
    this.silentCheckCount = 0;
    this.silenceReported = false;
    this.wasAudioActive = false;
  }

  setupAnalyser() {
    try {
      if (!this.audioElement?.srcObject) return;

      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      const source = this.audioContext.createMediaStreamSource(this.audioElement.srcObject);
      source.connect(this.analyser);
    } catch (error) {
      console.warn('Failed to setup audio analyser:', error);
    }
  }

  checkAudioHealth() {
    if (!this.audioElement) return;

    const state = {
      hasStream: !!this.audioElement.srcObject,
      isPlaying: !this.audioElement.paused,
      readyState: this.audioElement.readyState,
      volume: this.audioElement.volume,
      muted: this.audioElement.muted,
    };

    // Check if we have actual audio data
    let hasAudioData = false;
    if (this.analyser) {
      const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(dataArray);
      hasAudioData = dataArray.some(v => v > 5);
    }

    const isAudioActive = state.hasStream && state.isPlaying && !state.muted && state.readyState >= 2;

    if (isAudioActive && hasAudioData) {
      if (this.silenceReported && this.wasAudioActive) {
        this.options.onAudioRecovered();
      }
      this.silentCheckCount = 0;
      this.silenceReported = false;
      this.wasAudioActive = true;
    } else if (this.wasAudioActive) {
      // Only count toward silence after we've actually heard audio at
      // least once on this call. Without this guard we incremented every
      // tick from the start of the call (before either side had spoken),
      // which fired false "Audio may have stopped" toasts on consults
      // that simply began with quiet listening.
      this.silentCheckCount++;

      // Fire onSilenceDetected exactly once per silence streak; the
      // silenceReported latch is cleared in the recovery branch above.
      if (
        !this.silenceReported &&
        this.silentCheckCount >= this.options.silenceThreshold
      ) {
        this.silenceReported = true;
        this.options.onSilenceDetected({
          silentChecks: this.silentCheckCount,
          state,
          hasAudioData,
        });
      }
    }
  }

  getState() {
    if (!this.audioElement) return null;

    return {
      hasStream: !!this.audioElement.srcObject,
      isPlaying: !this.audioElement.paused,
      readyState: this.audioElement.readyState,
      volume: this.audioElement.volume,
      muted: this.audioElement.muted,
      currentTime: this.audioElement.currentTime,
    };
  }
}

/**
 * Connection health monitor for tracking ICE/WebRTC connection state
 */
export class ConnectionHealthMonitor {
  constructor(options = {}) {
    this.options = {
      onConnectionStateChange: options.onConnectionStateChange || (() => {}),
      onNetworkQualityChange: options.onNetworkQualityChange || (() => {}),
      onReconnectionNeeded: options.onReconnectionNeeded || (() => {}),
    };

    this.connectionState = ConnectionState.DISCONNECTED;
    this.networkQuality = NetworkQuality.GOOD;
    this.poorConnectionCount = 0;
    this.lastConnectionTime = null;
    this.reconnectionAttempts = 0;
  }

  setConnectionState(state) {
    const previousState = this.connectionState;
    this.connectionState = state;

    if (state === ConnectionState.CONNECTED) {
      this.lastConnectionTime = Date.now();
      this.reconnectionAttempts = 0;
    }

    this.options.onConnectionStateChange(state, previousState);
  }

  handlePoorConnection() {
    this.poorConnectionCount++;

    const criticalThreshold =
      VIDEO_CALL_CONFIG.POOR_CONNECTION_RECONNECT_THRESHOLD;
    // Hold quality at FAIR for the first couple of samples to absorb
    // single-window blips before showing the user a "Poor connection" banner.
    const poorThreshold = Math.max(2, Math.floor(criticalThreshold / 3));

    if (this.poorConnectionCount >= criticalThreshold) {
      this.networkQuality = NetworkQuality.CRITICAL;
      this.options.onNetworkQualityChange(NetworkQuality.CRITICAL);

      if (this.reconnectionAttempts < VIDEO_CALL_CONFIG.MAX_RECONNECTION_ATTEMPTS) {
        this.options.onReconnectionNeeded();
      }
    } else if (this.poorConnectionCount >= poorThreshold) {
      this.networkQuality = NetworkQuality.POOR;
      this.options.onNetworkQualityChange(NetworkQuality.POOR);
    } else {
      this.networkQuality = NetworkQuality.FAIR;
      this.options.onNetworkQualityChange(NetworkQuality.FAIR);
    }
  }

  handleGoodConnection() {
    this.poorConnectionCount = 0;
    this.networkQuality = NetworkQuality.GOOD;
    this.options.onNetworkQualityChange(NetworkQuality.GOOD);
  }

  // Called on every metric tick that comes back clean. Decays the counter
  // and promotes quality back up so the banner doesn't get stuck after a
  // single bad burst on an otherwise fine connection.
  handleGoodMetricTick() {
    if (this.poorConnectionCount === 0) return;

    this.poorConnectionCount = Math.max(
      0,
      this.poorConnectionCount -
        VIDEO_CALL_CONFIG.POOR_CONNECTION_DECAY_PER_GOOD_TICK
    );

    if (this.poorConnectionCount === 0) {
      this.networkQuality = NetworkQuality.GOOD;
      this.options.onNetworkQualityChange(NetworkQuality.GOOD);
    } else if (
      this.poorConnectionCount <
      Math.max(2, Math.floor(VIDEO_CALL_CONFIG.POOR_CONNECTION_RECONNECT_THRESHOLD / 3))
    ) {
      this.networkQuality = NetworkQuality.FAIR;
      this.options.onNetworkQualityChange(NetworkQuality.FAIR);
    }
  }

  incrementReconnectionAttempt() {
    this.reconnectionAttempts++;
    return this.reconnectionAttempts;
  }

  canReconnect() {
    return this.reconnectionAttempts < VIDEO_CALL_CONFIG.MAX_RECONNECTION_ATTEMPTS;
  }

  reset() {
    this.connectionState = ConnectionState.DISCONNECTED;
    this.networkQuality = NetworkQuality.GOOD;
    this.poorConnectionCount = 0;
    this.reconnectionAttempts = 0;
  }
}

/**
 * Device manager for handling device failover and recovery
 */
export class DeviceManager {
  constructor(meetingSession, options = {}) {
    this.meetingSession = meetingSession;
    this.options = {
      onDeviceError: options.onDeviceError || (() => {}),
      onDeviceRecovered: options.onDeviceRecovered || (() => {}),
      onDeviceChanged: options.onDeviceChanged || (() => {}),
    };

    this.currentAudioInput = null;
    this.currentVideoInput = null;
    this.currentAudioOutput = null;
    this.deviceChangeHandler = null;
  }

  async initializeDevices() {
    const [audioInputs, videoInputs, audioOutputs] = await Promise.all([
      this.meetingSession.audioVideo.listAudioInputDevices(),
      this.meetingSession.audioVideo.listVideoInputDevices(),
      this.meetingSession.audioVideo.listAudioOutputDevices(),
    ]);

    return { audioInputs, videoInputs, audioOutputs };
  }

  /**
   * Try to start audio input with fallback devices
   */
  async startAudioInputWithFallback(preferredDeviceId = null) {
    const devices = await this.meetingSession.audioVideo.listAudioInputDevices();

    if (devices.length === 0) {
      throw new Error('No audio input devices found');
    }

    // Try preferred device first
    if (preferredDeviceId) {
      const preferredDevice = devices.find(d => d.deviceId === preferredDeviceId);
      if (preferredDevice) {
        try {
          await this.meetingSession.audioVideo.startAudioInput(preferredDevice.deviceId);
          this.currentAudioInput = preferredDevice;
          return preferredDevice;
        } catch (error) {
          console.warn('Preferred audio device failed, trying fallback:', error);
        }
      }
    }

    // Try each device until one works
    for (const device of devices) {
      try {
        await this.meetingSession.audioVideo.startAudioInput(device.deviceId);
        this.currentAudioInput = device;
        this.options.onDeviceRecovered('audioInput', device);
        return device;
      } catch (error) {
        console.warn(`Audio device ${device.label} failed:`, error);
      }
    }

    // Last resort: try default
    try {
      await this.meetingSession.audioVideo.startAudioInput(null);
      this.currentAudioInput = { deviceId: 'default', label: 'Default Microphone' };
      return this.currentAudioInput;
    } catch (error) {
      this.options.onDeviceError('audioInput', error);
      throw new Error('Failed to start any audio input device');
    }
  }

  /**
   * Try to start video input with fallback devices
   */
  async startVideoInputWithFallback(preferredDeviceId = null) {
    const devices = await this.meetingSession.audioVideo.listVideoInputDevices();

    if (devices.length === 0) {
      console.warn('No video input devices found');
      return null;
    }

    // Try preferred device first
    if (preferredDeviceId) {
      const preferredDevice = devices.find(d => d.deviceId === preferredDeviceId);
      if (preferredDevice) {
        try {
          await this.meetingSession.audioVideo.startVideoInput(preferredDevice.deviceId);
          this.currentVideoInput = preferredDevice;
          return preferredDevice;
        } catch (error) {
          console.warn('Preferred video device failed, trying fallback:', error);
        }
      }
    }

    // Try each device until one works
    for (const device of devices) {
      try {
        await this.meetingSession.audioVideo.startVideoInput(device.deviceId);
        this.currentVideoInput = device;
        this.options.onDeviceRecovered('videoInput', device);
        return device;
      } catch (error) {
        console.warn(`Video device ${device.label} failed:`, error);
      }
    }

    this.options.onDeviceError('videoInput', new Error('Failed to start video'));
    return null;
  }

  /**
   * Set audio output with fallback
   */
  async setAudioOutputWithFallback(preferredDeviceId = null) {
    // Skip on iOS
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return null;
    }

    const devices = await this.meetingSession.audioVideo.listAudioOutputDevices();

    if (devices.length === 0) {
      console.warn('No audio output devices found');
      return null;
    }

    // Try preferred device first
    if (preferredDeviceId) {
      const preferredDevice = devices.find(d => d.deviceId === preferredDeviceId);
      if (preferredDevice) {
        try {
          await this.meetingSession.audioVideo.chooseAudioOutput(preferredDevice.deviceId);
          this.currentAudioOutput = preferredDevice;
          return preferredDevice;
        } catch (error) {
          console.warn('Preferred audio output failed, trying fallback:', error);
        }
      }
    }

    // Try default device
    try {
      await this.meetingSession.audioVideo.chooseAudioOutput(devices[0].deviceId);
      this.currentAudioOutput = devices[0];
      return devices[0];
    } catch (error) {
      this.options.onDeviceError('audioOutput', error);
      return null;
    }
  }

  /**
   * Listen for device changes
   */
  startDeviceChangeListener() {
    if (!navigator.mediaDevices) return;

    this.deviceChangeHandler = async () => {
      await sleep(VIDEO_CALL_CONFIG.DEVICE_REFRESH_DELAY);

      try {
        const devices = await this.initializeDevices();
        this.options.onDeviceChanged(devices);

        // Check if current devices are still available
        if (this.currentAudioInput) {
          const audioStillAvailable = devices.audioInputs.some(
            d => d.deviceId === this.currentAudioInput.deviceId
          );
          if (!audioStillAvailable && devices.audioInputs.length > 0) {
            console.warn('Current audio input disconnected, switching to fallback');
            await this.startAudioInputWithFallback();
          }
        }
      } catch (error) {
        console.error('Error handling device change:', error);
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }

  stopDeviceChangeListener() {
    if (this.deviceChangeHandler && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }
  }
}

/**
 * Enhanced audio binding with comprehensive retry logic
 */
export const bindAudioElementWithRetry = async (
  meetingSession,
  audioElement,
  options = {}
) => {
  const {
    maxRetries = VIDEO_CALL_CONFIG.MAX_AUDIO_BIND_RETRIES,
    onProgress = () => {},
    onSuccess = () => {},
    onError = () => {},
  } = options;

  if (!audioElement) {
    throw new Error('Audio element is required');
  }

  // Configure audio element
  audioElement.autoplay = true;
  audioElement.playsInline = true;
  audioElement.muted = false;
  audioElement.volume = 1.0;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return retryWithBackoff(
    async (attempt) => {
      onProgress(attempt, maxRetries);

      await meetingSession.audioVideo.bindAudioElement(audioElement);

      // Verify binding worked
      if (audioElement.srcObject) {
        // Try to play on mobile
        if (isMobile) {
          try {
            await audioElement.play();
          } catch (playError) {
            // Expected on mobile without user interaction
            console.info('Audio autoplay blocked, user interaction required');
            return { success: true, needsUserInteraction: true, playError };
          }
        }

        onSuccess();
        return { success: true, needsUserInteraction: false };
      }

      // No srcObject yet - may be waiting for remote audio
      if (attempt < maxRetries) {
        throw new Error('Audio not yet bound, retrying...');
      }

      return { success: true, needsUserInteraction: isMobile };
    },
    {
      maxRetries,
      baseDelay: VIDEO_CALL_CONFIG.AUDIO_BIND_RETRY_DELAY,
      onRetry: (attempt, delay, error) => {
        console.warn(`Audio bind attempt ${attempt} failed, retrying in ${delay}ms:`, error);
      },
    }
  ).catch((error) => {
    onError(error);
    throw error;
  });
};

/**
 * Play speaker test with multiple frequency beeps
 */
export const playSpeakerTest = async () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      // Envelope
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.03);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const now = audioContext.currentTime;

    // Two-tone confirmation beep
    playTone(880, now, 0.15);
    playTone(1100, now + 0.2, 0.15);

    // Clean up after sounds finish
    setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 600);

    return true;
  } catch (error) {
    console.error('Speaker test failed:', error);
    return false;
  }
};

/**
 * Safely get user media with timeout and retry
 */
export const getUserMediaSafe = async (constraints, timeoutMs = 10000) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('getUserMedia not supported');
  }

  return withTimeout(
    navigator.mediaDevices.getUserMedia(constraints),
    timeoutMs,
    'Getting media devices timed out'
  );
};

/**
 * Check if browser supports required WebRTC features
 */
export const checkWebRTCSupport = () => {
  const support = {
    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    RTCPeerConnection: !!window.RTCPeerConnection,
    AudioContext: !!(window.AudioContext || window.webkitAudioContext),
    WebSocket: !!window.WebSocket,
  };

  const isSupported = Object.values(support).every(Boolean);

  return {
    isSupported,
    details: support,
    missingFeatures: Object.entries(support)
      .filter(([, supported]) => !supported)
      .map(([feature]) => feature),
  };
};

/**
 * Format device label for display (removes device ID suffix)
 */
export const formatDeviceLabel = (label) => {
  if (!label) return 'Unknown Device';
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim() || 'Unknown Device';
};

/**
 * Get connection quality description
 */
// Short label shown on the in-call banner. Designed for at-a-glance use
// by clinicians — full diagnostic detail is logged to the console via
// getConnectionQualityDetail() for developers/support to inspect.
export const getConnectionQualityDescription = (quality) => {
  switch (quality) {
    case NetworkQuality.EXCELLENT:
      return 'Excellent call quality';
    case NetworkQuality.GOOD:
      return 'Good call quality';
    case NetworkQuality.FAIR:
      return 'Brief packet loss';
    case NetworkQuality.POOR:
      return 'Network unstable';
    case NetworkQuality.CRITICAL:
      return 'Reconnecting…';
    default:
      return 'Unknown connection quality';
  }
};

// Verbose explanation written only to the console so the network team can
// see what's happening without showing a wall of text to the doctor.
export const getConnectionQualityDetail = (quality) => {
  switch (quality) {
    case NetworkQuality.POOR:
      return "Network unstable: audio packets are being lost on the way to/from the call. This isn't broadband speed — it's Wi-Fi interference, VPN, or network path issues.";
    case NetworkQuality.CRITICAL:
      return 'Critical packet loss — attempting automatic reconnection.';
    case NetworkQuality.FAIR:
      return 'Brief packet loss detected; audio is still healthy.';
    default:
      return null;
  }
};
